import { FormTextField } from '@orbis-systems/orbis-react-toolkit';
import { ErrorMessage, FormikProps } from 'formik';
import React from 'react';

import { getCurrentUser } from '@app/redux/auth/auth.selectors';
import { useAppSelector } from '@app/redux/hooks';

import { ButtonBase, Grid, Typography } from '@mui/material';

import Form from '@components/forms';
import SubmitButton from '@components/submitButton';

import useVerifyEmailForm from './hooks';
import { VerifyEmailFormProps, VerifyEmailFormValues } from './interfaces';
import { BackSubmitButton } from './styles';

function VerifyEmailForm({ initialValues, onSubmit, ...restProps }: VerifyEmailFormProps) {
	const formState = useVerifyEmailForm({ initialValues, onSubmit });
	const currentUser = useAppSelector(getCurrentUser);

	return <VerifyEmailContainer {...formState} {...restProps} email={currentUser?.email} />;
}

interface VerifyEmailContainerProps {
	handleSendVerificationEmailCode: () => void;
	formik: FormikProps<VerifyEmailFormValues>;
	isSendingVerificationEmailCode: boolean;
	onDecline?: () => void;
	email?: string;
}

export const VerifyEmailContainer = ({
	handleSendVerificationEmailCode,
	isSendingVerificationEmailCode,
	onDecline,
	formik,
	email,
}: VerifyEmailContainerProps) => {
	const isSubmitDisabled = formik.isSubmitting || isSendingVerificationEmailCode;

	return (
		<Form formik={formik}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="subtitle1">
						<b>Verify email address</b>
					</Typography>
				</Grid>

				{email ? (
					<Grid item xs={12}>
						<Typography variant="body1" sx={{ fontSize: 16, fontWeight: 400 }}>
							Enter the verification code sent to <b>{email}</b>
						</Typography>
					</Grid>
				) : null}

				<Grid item xs={12}>
					<FormTextField
						focused
						name={'code'}
						label={'Verification code'}
						autoComplete="off"
						disabled={isSubmitDisabled}
					/>
				</Grid>

				<Grid item xs={12} textAlign={'right'}>
					<ButtonBase onClick={handleSendVerificationEmailCode} disabled={isSendingVerificationEmailCode}>
						<Typography variant="body2">
							<b>Resend code?</b>
						</Typography>
					</ButtonBase>
				</Grid>

				<Grid
					item
					xs={12}
					sx={{
						color: theme => theme.palette.error.main,
						fontSize: 14,
					}}
				>
					<ErrorMessage name="resendVerificationEmailCodeError" />
				</Grid>

				<Grid item xs={12}>
					<Grid container spacing={3}>
						{onDecline && (
							<Grid item xs={6}>
								<BackSubmitButton
									fullWidth
									type="button"
									variant="contained"
									color="secondary"
									onClick={onDecline}
									disabled={isSubmitDisabled}
								>
									Back
								</BackSubmitButton>
							</Grid>
						)}

						<Grid item xs={onDecline ? 6 : 12}>
							<SubmitButton label={'Verify'} disabled={isSubmitDisabled} />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Form>
	);
};

export default VerifyEmailForm;
